import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Server } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { Channel, Provider, ProxyNode, StreamUrl } from '@lib/streamFlowApi';
import HLSPlayer from '@components/streamflow/HLSPlayer';
import MirrorSwitcher from '@components/streamflow/MirrorSwitcher';
import { isTvMode } from '@lib/runtime';

export default function StreamPlayer() {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamMeta, setStreamMeta] = useState<StreamUrl | null>(null);
  const [proxyNodes, setProxyNodes] = useState<ProxyNode[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  // Mirror failover state.
  const [availableProviderIds, setAvailableProviderIds] = useState<string[]>([]);
  const [currentProviderId, setCurrentProviderId] = useState<string | null>(null);
  const triedRef = useRef<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const watchHistoryIdRef = useRef<string | null>(null);
  const memberIdRef = useRef<string | null>(null);

  /**
   * Mint a stream URL for a given provider preference. Used both for the
   * initial load (no preference) and for mirror switching (explicit pid).
   */
  const mintStream = useCallback(async (memberId: string, preferredProviderId?: string) => {
    if (!channelId) return;
    const streamRes = await streamFlowApi.getStreamUrl({
      channel_id: channelId,
      member_id: memberId,
      preferred_provider_id: preferredProviderId,
    });
    if (!streamRes.success || !streamRes.data) {
      setError(streamRes.error?.message || 'Failed to get stream URL.');
      return;
    }
    setStreamUrl(streamRes.data.stream_url);
    setStreamMeta(streamRes.data);
    setCurrentProviderId(streamRes.data.provider_id);
    if (streamRes.data.available_provider_ids?.length) {
      setAvailableProviderIds(streamRes.data.available_provider_ids);
    }
    triedRef.current.add(streamRes.data.provider_id);

    // Record watch session on first successful mint only.
    if (!watchHistoryIdRef.current) {
      const histRes = await streamFlowApi.recordWatchStart({
        memberId,
        channelId,
        providerId: streamRes.data.provider_id,
        device: 'web',
      });
      if (histRes.success && histRes.data) watchHistoryIdRef.current = histRes.data.id;
    }
  }, [channelId]);

  useEffect(() => {
    if (!channelId) return;
    const id = channelId;
    triedRef.current = new Set();

    async function loadStream() {
      setLoading(true);
      setError(null);

      const chRes = await streamFlowApi.listChannels({ search: '', limit: 200 });
      const ch = (chRes.data || []).find((c: Channel) => c.id === id);
      if (ch) setChannel(ch);

      const memRes = await streamFlowApi.listMembers();
      const members = memRes.data || [];
      if (members.length === 0) {
        setError('No family members configured. Add a member first.');
        setLoading(false);
        return;
      }
      memberIdRef.current = members[0].id;
      await mintStream(members[0].id);
      setLoading(false);
    }

    loadStream();

    // Pre-load proxy nodes + providers for the "via {node}" badge and
    // the MirrorSwitcher's display labels.
    Promise.all([streamFlowApi.listProxyNodes(), streamFlowApi.listProviders()]).then(([pnRes, prRes]) => {
      if (pnRes.success) setProxyNodes(pnRes.data || []);
      if (prRes.success) setProviders(prRes.data || []);
    });

    return () => {
      const historyId = watchHistoryIdRef.current;
      if (historyId) {
        streamFlowApi.recordWatchEnd(historyId).catch(() => {});
        watchHistoryIdRef.current = null;
      }
    };
  }, [channelId, mintStream]);

  /**
   * On a fatal HLS error, try the next mirror in priority order. Loop
   * terminates when every available provider has been tried.
   */
  const handleFatalPlaybackError = useCallback(() => {
    if (!memberIdRef.current || availableProviderIds.length === 0) return;
    // Find the next untried provider after the current one.
    const next = availableProviderIds.find((pid) => !triedRef.current.has(pid));
    if (!next) {
      toast.error('No working mirror available for this channel');
      return;
    }
    toast(
      `Switching to backup mirror (${providers.find((p) => p.id === next)?.name ?? 'next source'})…`,
      { id: 'mirror-switch', icon: '📡' },
    );
    mintStream(memberIdRef.current, next);
  }, [availableProviderIds, providers, mintStream]);

  /** Manual switch from MirrorSwitcher dropdown. */
  const handleManualSwitch = useCallback((providerId: string) => {
    if (!memberIdRef.current) return;
    triedRef.current.delete(providerId); // allow re-trying after a manual switch
    mintStream(memberIdRef.current, providerId);
  }, [mintStream]);

  // TV mode = full-screen player on a black background, no chrome.
  // Channel name + status overlay top-left for ~2s on load (handled
  // via the loading state); otherwise just the player and a subtle
  // back hint at top-right.
  const tvMode = isTvMode();

  if (tvMode) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        {loading ? (
          <div className="text-white text-2xl">Loading {channel?.name ?? 'stream'}…</div>
        ) : error ? (
          <div className="text-center text-white">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <p className="text-2xl mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-white text-gray-900 rounded-lg text-lg"
            >
              Back to Channels
            </button>
          </div>
        ) : streamUrl ? (
          <div className="w-full h-full">
            <HLSPlayer
              url={streamUrl}
              channelName={channel?.name}
              onError={(err) => {
                if (err.type === 'fatal') handleFatalPlaybackError();
                else toast.error(err.message);
              }}
              onBack={() => navigate('/')}
            />
            {/* Channel name strip — fades after 3s, reappears on activity. */}
            {channel && (
              <div className="absolute top-6 left-6 right-6 flex items-center gap-3 pointer-events-none">
                {channel.logo_url && <img src={channel.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-black/40 p-1" />}
                <span className="text-white text-2xl font-semibold drop-shadow-lg">{channel.name}</span>
              </div>
            )}
            <div className="absolute bottom-4 right-6 text-white/60 text-sm pointer-events-none">
              ← Back · ↑↓ Channel · OK Play/Pause
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      {channel && (
        <div className="flex items-center gap-3 mb-4">
          {channel.logo_url && (
            <img src={channel.logo_url} alt="" className="w-10 h-10 rounded object-contain bg-gray-100" />
          )}
          <div className="flex-1">
            <h1 className="text-xl font-bold">{channel.name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {channel.category && <p className="text-sm text-gray-500">{channel.category}</p>}
              {streamMeta?.stream_mode && (() => {
                // Extract the playback host so the user has direct
                // visual evidence of where bytes are coming from. "via
                // Azure edge" + an actual `.ts.net` host would surface
                // a bug; "via tk-bakery" + the matching tailnet host
                // confirms correct routing.
                let playbackHost = '';
                try { playbackHost = new URL(streamMeta.stream_url).hostname; } catch { /* noop */ }
                const isTailnetHost = playbackHost.endsWith('.ts.net');
                const label = streamMeta.stream_mode === 'tailscale-direct'
                  ? 'Direct (NAS)'
                  : streamMeta.proxy_node_id
                    ? `via ${proxyNodes.find(n => n.id === streamMeta.proxy_node_id)?.name ?? 'proxy node'}`
                    : 'via Azure edge';
                const tone = streamMeta.stream_mode === 'azure-edge'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : isTailnetHost
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-700 border-slate-200';
                return (
                  <span
                    title={`Playback host: ${playbackHost}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tone}`}
                  >
                    <Server className="w-3 h-3" />
                    {label}
                    {playbackHost && <span className="font-mono font-normal opacity-70">· {playbackHost}</span>}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
          <p className="text-white text-sm">Loading stream...</p>
        </div>
      ) : error ? (
        <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
          <div className="text-center text-white">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
            <p className="text-sm mb-3">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg text-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      ) : streamUrl ? (
        <>
          {/* Live channels are HLS (.m3u8); VOD movies are progressive
              MP4 served by Xtream's /movie/ path. The browser's native
              <video> handles MP4 fine — no HLS.js needed for VOD. We
              branch on channel.kind so the right player loads. */}
          {channel?.kind === 'vod' ? (
            <div className="relative w-full bg-black rounded-lg overflow-hidden">
              <video
                src={streamUrl}
                controls
                autoPlay
                className="w-full aspect-video bg-black"
                onError={() => {
                  toast.error('Movie playback failed — trying next mirror…');
                  handleFatalPlaybackError();
                }}
              />
            </div>
          ) : (
            <HLSPlayer
              url={streamUrl}
              channelName={channel?.name}
              onError={(err) => {
                if (err.type === 'fatal') handleFatalPlaybackError();
                else toast.error(err.message);
              }}
              onBack={() => navigate('/')}
            />
          )}
          {availableProviderIds.length > 1 && currentProviderId && (
            <div className="mt-3">
              <MirrorSwitcher
                providers={providers}
                availableProviderIds={availableProviderIds}
                currentProviderId={currentProviderId}
                onSwitch={handleManualSwitch}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
